import { Injectable, signal, computed } from '@angular/core';
import {
    signUp,
    confirmSignUp,
    autoSignIn,
    signIn,
    confirmSignIn,
    resendSignUpCode,
    signOut,
    fetchAuthSession,
    getCurrentUser,
} from 'aws-amplify/auth';

export type AuthStep = 'identifier' | 'otp' | 'authenticated';
export type OtpDelivery = 'email' | 'sms' | 'both';

export interface SignUpInput {
    name: string;
    email: string;
    /** E.164 format, e.g. +972543050580. */
    phone: string;
    /** Parent referral organization ("גורם מפנה"). */
    organizationId?: number;
    /** Branch under that organization ("סניף"). */
    referralSourceId?: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    /** Current point in the OTP flow. Drives the login UI. */
    readonly step = signal<AuthStep>('identifier');
    /** Which channels delivered the OTP — used by the OTP screen subtitle. */
    readonly otpDelivery = signal<OtpDelivery>('email');
    /**
     * Cognito username for the in-flight flow. During signUp this is a UUID
     * (pool uses alias_attributes); during login this is whatever the user
     * typed (email or phone, which Cognito resolves via alias).
     */
    readonly pendingIdentifier = signal<string>('');
    /**
     * Human-readable form to show on the OTP screen ("we sent a code to ___").
     * Distinct from pendingIdentifier because the cognito username can be a
     * UUID the user wouldn't recognise.
     */
    readonly pendingDisplayIdentifier = signal<string>('');
    /** Last error message (i18n key or raw text). */
    readonly lastError = signal<string | null>(null);

    readonly isAuthenticated = computed(() => this.step() === 'authenticated');

    /**
     * Step 1 (login): send the user's identifier (email or E.164 phone) to
     * Cognito. Cognito calls our create-auth-challenge Lambda, which sends a
     * 6-digit code to every channel on the user's profile (email + SMS when
     * both exist; SMS only when no email). On success, advances `step` to
     * 'otp'.
     *
     * If the identifier is unknown, throws 'login.errors.userNotFound' so the
     * caller can route to /signup. (prevent_user_existence_errors=LEGACY on
     * the user pool client makes Cognito surface this distinctly.)
     */
    async sendIdentifier(identifier: string): Promise<void> {
        this.lastError.set(null);
        try {
            const result = await signIn({
                username: identifier,
                options: {
                    authFlowType: 'CUSTOM_WITHOUT_SRP',
                },
            });
            if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
                this.otpDelivery.set(
                    parseOtpDelivery(result.nextStep.additionalInfo?.['delivery']),
                );
            }
            this.pendingIdentifier.set(identifier);
            this.pendingDisplayIdentifier.set(identifier);
            this.step.set('otp');
        } catch (err: unknown) {
            // Log for diagnostics — Amplify v6's error shapes vary between
            // raw Cognito error names, AmplifyErrorCode enums, and wrapped
            // messages; logging makes future "what does this throw?" cheap.
            console.warn('[auth] signIn failed', err);
            const error = err as { name?: string; code?: string; message?: string };
            const indicators = [error.name, error.code, error.message]
                .filter((v): v is string => typeof v === 'string')
                .join(' ');
            // Match any signal that the identifier is unknown. Cognito raw
            // name is "UserNotFoundException"; Amplify sometimes surfaces it
            // as the literal message "User does not exist." Both routes go
            // to /signup silently — the user shouldn't have to learn that
            // their account doesn't exist.
            if (
                /UserNotFoundException|UserNotFound\b|User does not exist/i.test(
                    indicators,
                )
            ) {
                throw new Error('login.errors.userNotFound');
            }
            if (/UserNotConfirmedException|UserNotConfirmed/i.test(indicators)) {
                throw new Error('login.errors.userNotConfirmed');
            }
            this.lastError.set(error.message ?? 'login.errors.generic');
            throw err;
        }
    }

    /**
     * Step 1 (signup): create the Cognito account. Cognito sends a
     * verification code to the user's email automatically (driven by
     * auto_verified_attributes=["email"] on the pool). The user must then
     * call confirmSignUpCode() with that code.
     *
     * The pool uses alias_attributes, so the Cognito username is a fresh
     * UUID we generate here; email and phone become verifiable aliases.
     * The signup form already prevented duplicate-submit with `submitting()`
     * — if the user really creates two accounts (e.g. resubmit after a
     * timeout), Cognito ends up with two UNCONFIRMED users sharing the
     * same email/phone; the UNCONFIRMED ones age out after 7 days.
     *
     * The password is a high-entropy throwaway: CUSTOM_AUTH-only clients
     * (ALLOW_CUSTOM_AUTH + ALLOW_REFRESH_TOKEN_AUTH) never invoke it.
     */
    async signUpUser(input: SignUpInput): Promise<void> {
        this.lastError.set(null);
        const { givenName, familyName } = splitDisplayName(input.name);
        const password = crypto.randomUUID() + 'Aa1!';
        // Pool uses alias_attributes (not username_attributes), so the
        // username is a generated UUID separate from email/phone. Cognito
        // resolves signIn via the verified email/phone aliases later.
        const username = crypto.randomUUID();
        try {
            await signUp({
                username,
                password,
                options: {
                    userAttributes: {
                        email: input.email,
                        phone_number: input.phone,
                        name: input.name,
                        given_name: givenName,
                        ...(familyName && { family_name: familyName }),
                        // Cognito custom attributes are strings; backend reads them
                        // from the IdToken on first /users/me to create a pending user.
                        ...(input.organizationId != null && {
                            'custom:organization_id': String(input.organizationId),
                        }),
                        ...(input.referralSourceId != null && {
                            'custom:referral_source_id': String(input.referralSourceId),
                        }),
                    },
                    // No autoSignIn: the pool is CUSTOM_AUTH-only, so post-confirm login
                    // is done explicitly via signIn(CUSTOM_WITHOUT_SRP) in confirmSignUpCode().
                },
            });
            // For confirmSignUp / resendSignUpCode, the "username" Cognito
            // expects is the same one we used at signUp (the UUID). It's
            // not the alias — Cognito's docs are misleading here. Cache it
            // so the verify step can use it.
            this.pendingIdentifier.set(username);
            // Keep a human-readable identifier separate from the cognito
            // username so the verify screen can show "we emailed <email>".
            this.pendingDisplayIdentifier.set(input.email);
        } catch (err: unknown) {
            const error = err as { name?: string; message?: string };
            // Cognito's AliasExistsException fires when another user already
            // owns the email or phone — that user has either confirmed (so
            // they should log in instead) or is UNCONFIRMED and aging out.
            // Surface a single message that routes them to /login.
            if (error.name === 'AliasExistsException') {
                throw new Error('login.errors.alreadyRegistered');
            }
            this.lastError.set(error.message ?? 'login.errors.generic');
            throw err;
        }
    }

    /**
     * Step 2 (signup): submit the verification code Cognito sent during
     * signUpUser(). On success, autoSignIn() completes the login (no
     * extra OTP round-trip) and `step` advances to 'authenticated'.
     */
    async confirmSignUpCode(code: string): Promise<void> {
        this.lastError.set(null);
        const username = this.pendingIdentifier();
        if (!username) {
            throw new Error('login.errors.generic');
        }
        // Phase A: confirm the user. Errors here are about the verification
        // code itself — invalid, expired, etc. The user stays on the verify
        // screen and either re-enters or resends.
        try {
            await confirmSignUp({ username, confirmationCode: code });
        } catch (err: unknown) {
            const error = err as { name?: string; message?: string };
            if (error.name === 'CodeMismatchException') {
                throw new Error('login.errors.invalidCode');
            }
            if (error.name === 'ExpiredCodeException') {
                throw new Error('login.errors.codeExpired');
            }
            this.lastError.set(error.message ?? 'login.errors.generic');
            throw err;
        }

        // Phase B: try autoSignIn. This only works if the SPA still holds the
        // staged session that signUp({ autoSignIn: true }) installed. A page
        // reload between signUp and confirmSignUp wipes that state, and the
        // user pool was recreated mid-session too — both can land us here
        // with a confirmed account but no auto-login. The fall-through is
        // 'login.errors.autoSignInUnavailable', which the caller maps to a
        // redirect to /login (where our CUSTOM_AUTH flow handles login).
        try {
            const result = await autoSignIn();
            if (result.isSignedIn) {
                this.step.set('authenticated');
                return;
            }
            console.warn('[auth] autoSignIn returned without isSignedIn', result);
        } catch (err: unknown) {
            console.warn('[auth] autoSignIn failed after confirmSignUp', err);
        }
        throw new Error('login.errors.autoSignInUnavailable');
    }

    /** Re-send the Cognito signup verification code. */
    async resendSignUpVerification(): Promise<void> {
        const username = this.pendingIdentifier();
        if (!username) return;
        await resendSignUpCode({ username });
    }

    /**
     * Step 2: submit the 6-digit code. On success, Cognito issues JWTs and
     * `step` advances to 'authenticated'.
     */
    async submitOtp(code: string): Promise<void> {
        this.lastError.set(null);
        try {
            const result = await confirmSignIn({ challengeResponse: code });
            if (result.isSignedIn) {
                this.step.set('authenticated');
                return;
            }
            // Wrong code — Cognito keeps the session open for 2 more attempts.
            // The CUSTOM_AUTH challenge re-runs; the user can retry without a new email.
            throw new Error('login.errors.invalidCode');
        } catch (err: unknown) {
            const error = err as { name?: string; message?: string };
            if (
                error.name === 'NotAuthorizedException' ||
                error.message?.includes('Invalid session')
            ) {
                // Session expired or too many wrong attempts — restart the flow.
                this.step.set('identifier');
                throw new Error('login.errors.sessionExpired');
            }
            this.lastError.set(error.message ?? 'login.errors.generic');
            throw err;
        }
    }

    /** Restart the flow from the identifier step. */
    reset(): void {
        this.step.set('identifier');
        this.pendingIdentifier.set('');
        this.otpDelivery.set('email');
        this.lastError.set(null);
    }

    private signOutInFlight: Promise<void> | null = null;

    /**
     * Sign the user out. Concurrent callers (e.g. parallel HTTP requests all
     * receiving 401 at once) share a single in-flight promise so we don't run
     * Cognito's local sign-out N times in parallel.
     */
    async signOut(): Promise<void> {
        if (this.signOutInFlight) return this.signOutInFlight;
        this.signOutInFlight = (async () => {
            try {
                await signOut();
            } finally {
                this.reset();
                this.signOutInFlight = null;
            }
        })();
        return this.signOutInFlight;
    }

    /** Returns the current ID token (used by the HTTP interceptor). */
    async getIdToken(): Promise<string | null> {
        try {
            const session = await fetchAuthSession();
            return session.tokens?.idToken?.toString() ?? null;
        } catch {
            return null;
        }
    }

    /** Restore session on app boot. Called once from APP_INITIALIZER. */
    async restoreSession(): Promise<void> {
        try {
            await getCurrentUser();
            this.step.set('authenticated');
        } catch (err: unknown) {
            // Only treat the "no signed-in user" cases as an expected unauthenticated
            // state. Genuine errors (network, storage, misconfig) get logged so a
            // boot-time blip isn't silently masked as a logout — the guard will
            // still re-check via getIdToken() before granting access.
            const name = (err as { name?: string }).name;
            const expected =
                name === 'UserUnAuthenticatedException' ||
                name === 'NotAuthorizedException';
            if (expected) {
                this.step.set('identifier');
                return;
            }
            console.error('[auth] restoreSession failed', err);
        }
    }
}

/** Split a single display-name field into Cognito given/family name claims. */
function splitDisplayName(name: string): { givenName: string; familyName?: string } {
    const trimmed = name.trim();
    const spaceIdx = trimmed.indexOf(' ');
    if (spaceIdx === -1) {
        return { givenName: trimmed };
    }
    const familyName = trimmed.slice(spaceIdx + 1).trim();
    return {
        givenName: trimmed.slice(0, spaceIdx),
        ...(familyName ? { familyName } : {}),
    };
}

/** Map Cognito publicChallengeParameters.delivery to OTP screen copy. */
function parseOtpDelivery(delivery: string | undefined): OtpDelivery {
    if (!delivery) return 'email';
    const channels = delivery.split(',').map((s) => s.trim());
    if (channels.includes('email') && channels.includes('sms')) return 'both';
    if (channels.includes('sms')) return 'sms';
    return 'email';
}
